/**
 * Session Bridge utility for cross-origin session sharing between
 * React EMIS and Angular EMIS via a hidden iframe + postMessage.
 *
 * Mirrors the working tnemis approach (react-tnemis-fe sessionBridge.ts):
 * the Angular EMIS app hosts a receiver.html that this module talks to
 * through a hidden iframe. Angular EMIS serves receiver.html under
 * `/assets/receiver.html`. The Angular partner origin is resolved per
 * environment (LOCAL / STAGING / PRODUCTION).
 */

const OAUTH_ENDPOINT =
  'https://eeqzof4a8g.execute-api.ap-south-1.amazonaws.com/api/oauth_test';

// Angular EMIS serves receiver.html from the assets folder.
const RECEIVER_PATH = '/assets/receiver.html';

/**
 * Resolves the Angular EMIS origin for the current environment.
 * LOCAL -> localhost:4200, STAGING -> emis-staging, else PRODUCTION.
 */
export const getAngularOrigin = (): string =>
  (process.env.REACT_APP_API_environment === 'LOCAL'
    ? 'http://localhost:4200'
    : process.env.REACT_APP_API_environment === 'STAGING'
      ? 'https://emis-staging.tnsed.com'
      : 'https://emis.tnschools.gov.in'
  ).replace(/\/$/, '');

/**
 * Ensures AuthorizationKey is present in localStorage before session priming.
 * If missing, fetches it from the OAuth endpoint and stores it.
 * If the fetch fails, proceeds silently (don't block priming).
 */
async function ensureAuthorizationKey(): Promise<void> {
  const existingKey = localStorage.getItem('AuthorizationKey');
  if (existingKey) return;

  try {
    const response = await fetch(OAUTH_ENDPOINT);
    if (response.ok) {
      const data = await response.json();
      if (data.Authorization_key) {
        localStorage.setItem('AuthorizationKey', data.Authorization_key);
      }
    }
  } catch {
    // OAuth fetch failed — proceed without blocking priming
  }
}

/**
 * Primes the Angular EMIS session by posting all localStorage data
 * to the Angular receiver.html via a hidden iframe bridge.
 *
 * - Ensures AuthorizationKey is present (fetches from OAuth if missing)
 * - Creates a hidden iframe targeting `${angularOrigin}/assets/receiver.html`
 * - On load: posts JSON.stringify of all localStorage key-value pairs
 * - Resolves 'success' on ack, 'timeout' after 5s, 'error' on iframe error
 */
export async function primeAngularSession(): Promise<
  'success' | 'timeout' | 'error'
> {
  await ensureAuthorizationKey();

  return new Promise((resolve) => {
    const angularOrigin = getAngularOrigin();
    const iframe = document.createElement('iframe');
    iframe.src = `${angularOrigin}${RECEIVER_PATH}`;
    iframe.style.display = 'none';

    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      window.removeEventListener('message', onMessage);
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== angularOrigin) return;
      if (event.data === 'success' && !settled) {
        settled = true;
        cleanup();
        resolve('success');
      }
    };

    iframe.onload = () => {
      if (settled) return;
      const payload: Record<string, string> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          if (value !== null) {
            payload[key] = value;
          }
        }
      }
      iframe.contentWindow?.postMessage(JSON.stringify(payload), angularOrigin);
    };

    iframe.onerror = () => {
      if (!settled) {
        settled = true;
        cleanup();
        resolve('error');
      }
    };

    window.addEventListener('message', onMessage);
    document.body.appendChild(iframe);

    timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        cleanup();
        resolve('timeout');
      }
    }, 5000);
  });
}

/**
 * Clears the Angular EMIS session by sending COMMAND_CLEAR_STORAGE to the
 * Angular receiver.html via a hidden iframe bridge.
 *
 * Resolves 'clear_success' on ack or 'timeout' after 1s.
 */
export function clearAngularSession(): Promise<'clear_success' | 'timeout'> {
  return new Promise((resolve) => {
    const angularOrigin = getAngularOrigin();

    // Reuse an existing receiver iframe if present, otherwise create one.
    let iframe = document.querySelector<HTMLIFrameElement>(
      'iframe[src*="/assets/receiver.html"]'
    );
    const isExisting = !!iframe;

    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.src = `${angularOrigin}${RECEIVER_PATH}`;
      iframe.style.display = 'none';
    }

    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      window.removeEventListener('message', onMessage);
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== angularOrigin) return;
      if (event.data === 'clear_success' && !settled) {
        settled = true;
        cleanup();
        resolve('clear_success');
      }
    };

    const sendClearCommand = () => {
      if (settled) return;
      iframe!.contentWindow?.postMessage(
        'COMMAND_CLEAR_STORAGE',
        angularOrigin
      );
    };

    window.addEventListener('message', onMessage);

    if (isExisting) {
      sendClearCommand();
    } else {
      iframe.onload = sendClearCommand;
      document.body.appendChild(iframe);
    }

    timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        cleanup();
        resolve('timeout');
      }
    }, 1000);
  });
}
